import { t } from '@lingui/core/macro';
import { SolutionStatus, SolutionTag } from '@luminovo/api-contracts';
import { colorSystem, Flexbox, Text } from '@luminovo/design-system';
import React from 'react';

import { extractSolutionStatus, extractSolutionTagChips, SolutionTagChip, WarningStyledTags } from '../../extractors';
import { formatSolutionStatus } from '../../formatters';
import { Solution, SolutionErrorTags, SolutionWarningTags } from '../../types';
import { SolutionStatusCircleIcon } from './SolutionStatusIcon';
import { SolutionTagList } from './SolutionTagList';

export const SolutionStatusInformation: React.FunctionComponent<{
    solution: Pick<Solution, 'solutionTags'>;
}> = ({ solution }) => {
    const chips = extractSolutionTagChips(solution);

    const effectiveStatus = extractSolutionStatus(solution, 'effective');
    const originalStatus = extractSolutionStatus(solution, 'original');

    return (
        <SolutionStatusInformationInternal
            effectiveStatus={effectiveStatus}
            originalStatus={originalStatus}
            chips={chips}
        />
    );
};

export const SolutionStatusInformationInternal: React.FunctionComponent<{
    effectiveStatus: SolutionStatus;
    originalStatus: SolutionStatus;
    chips: SolutionTagChip[];
}> = ({ effectiveStatus, originalStatus, chips }) => {
    const filteredChips = chips.filter(
        (c) =>
            SolutionWarningTags.includes(c.solutionTag) ||
            SolutionErrorTags.includes(c.solutionTag) ||
            WarningStyledTags.includes(c.solutionTag) ||
            c.solutionTag === SolutionTag.NonCancellableNonReturnable,
    );

    return (
        <Flexbox
            gap={8}
            sx={{
                flexDirection: 'column',
                padding: '4px',
            }}
        >
            <Flexbox
                gap={4}
                sx={{
                    alignItems: 'center',
                }}
            >
                <SolutionStatusCircleIcon status={effectiveStatus} />
                <Text variant="h5">{formatSolutionStatus(effectiveStatus)}</Text>
            </Flexbox>
            {effectiveStatus !== originalStatus && (
                <Text variant={'caption'} color={colorSystem.neutral[7]}>
                    {t`Manually changed from ${formatSolutionStatus(originalStatus)}`}
                </Text>
            )}
            {filteredChips.length !== 0 && <SolutionTagList chips={filteredChips} />}
        </Flexbox>
    );
};
